import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { PortfolioEnterpriseGroupResult } from '@mews-commander/data-access';
import { LocalizedText, lt, showSuccessAlert } from '@mews/framework';
import { TextKey, RouteName } from '@mews-commander/enums';
import { useResolveRoute } from '@mews/routing';
import { createFormResolver } from '@mews-commander/form';
import { validation as validate } from '@mews-commander/legacy-form';
import { WithEnterpriseIds } from '../../../enterpriseRateManagement/types';
import { PropertyGroupDialogForm } from '../PropertyGroupDialog/PropertyGroupDialog';
import { DEFAULT_VALUES, FIELD_NAMES, FormValues } from '../PropertyGroupDialog/types/Form';
import { FORM_TYPE, getConstraints } from '../PropertyGroupDialog/constraints';
import { useAddPropertyGroup } from '../../queries/useAddPropertyGroup';
import { navigateInModal } from '../../../app/actions/common';
import { usePropertiesGroupsData, useUpdatePropertyGroup } from '../../queries';

interface PropertyGroupContainerProps extends WithEnterpriseIds {
    onSuccess?: () => void;
    isOpened: boolean;
    onConfirm: () => Promise<void>;
    onCancel: () => void;
    initialTabValue?: FORM_TYPE;
}

export const PropertyGroupContainer = ({
    enterpriseIds,
    isOpened,
    onConfirm,
    onCancel,
    onSuccess,
    initialTabValue = FORM_TYPE.CREATE_NEW,
}: PropertyGroupContainerProps) => {
    const [activeTabValue, setActiveTabValue] = useState<FORM_TYPE>(initialTabValue);
    const createPropertyGroup = useAddPropertyGroup();
    const updatePropertyGroup = useUpdatePropertyGroup();
    const resolveRoute = useResolveRoute();
    const dispatch = useDispatch();
    const { data: propertyGroups } = usePropertiesGroupsData();

    useEffect(() => {
        setActiveTabValue(initialTabValue);
    }, [initialTabValue]);

    const resolver = createFormResolver<FormValues>(
        validate(getConstraints(activeTabValue, propertyGroups?.data || []))
    );

    const formProps = {
        resolver,
        defaultValues: {
            ...DEFAULT_VALUES,
        },
    };

    const onCreateSuccess = (values: FormValues) => {
        setActiveTabValue(FORM_TYPE.CREATE_NEW);
        showSuccessAlert({
            title: lt('PropertyGroupCreated', { propertyGroupName: values.name }),
            action: {
                text: <LocalizedText textKey={TextKey.PropertyGroupCreatedActionText} />,
                onClick: () => dispatch(navigateInModal(resolveRoute(RouteName.EnterpriseRelationshipReportGroups))),
            },
        });
        onSuccess?.();
    };

    const onEditSuccess = (propertyGroup: PortfolioEnterpriseGroupResult, propertyIdsCount: number) => {
        setActiveTabValue(FORM_TYPE.CREATE_NEW);
        showSuccessAlert({
            title: lt('PropertiesAddedToPropertyGroup', {
                propertyGroupName: propertyGroup.name,
                count: propertyIdsCount,
            }),
            action: {
                text: <LocalizedText textKey={TextKey.PropertyGroupCreatedActionText} />,
                onClick: () => dispatch(navigateInModal(resolveRoute(RouteName.EnterpriseRelationshipReportGroups))),
            },
        });
        onSuccess?.();
    };

    const getPropertyIdsToAdd = (values: FormValues) => {
        const selectedPropertyGroup = propertyGroups?.data?.find((propertyGroup) => propertyGroup.id === values.id);

        if (!selectedPropertyGroup) {
            return [];
        }

        return enterpriseIds.filter((id) => !selectedPropertyGroup.propertyIds.includes(id));
    };

    const onSubmit = (values: FormValues) => {
        if (activeTabValue === FORM_TYPE.CREATE_NEW) {
            createPropertyGroup.mutate(
                { ...values, enterpriseIds },
                {
                    onSuccess: onCreateSuccess,
                }
            );

            return;
        }

        const propertiesToAdd = getPropertyIdsToAdd(values);

        updatePropertyGroup.mutate(
            { id: values[FIELD_NAMES.PROPERTY_GROUP_ID], propertyIdsToAdd: propertiesToAdd },
            {
                onSuccess: (propertyGroup) => onEditSuccess(propertyGroup, propertiesToAdd.length || 0),
            }
        );
    };

    const handleCancel = () => {
        setActiveTabValue(FORM_TYPE.CREATE_NEW);
        onCancel();
    };

    return (
        <PropertyGroupDialogForm
            useFormProps={formProps}
            onConfirm={onConfirm}
            onCancel={handleCancel}
            isLoading={createPropertyGroup.isPending || updatePropertyGroup.isPending}
            onSubmit={onSubmit}
            enterpriseIds={enterpriseIds}
            active={isOpened}
            activeTabValue={activeTabValue}
            setActiveTabValue={setActiveTabValue}
            propertyGroups={propertyGroups}
        />
    );
};
